/**
 * Step-through scripts for the ordered-flow diagrams (z8nrz7f3kc). Each step
 * names the diagram parts (`parts`) it lights; the diagram dims the rest.
 * Part keys are the `k` props in `diagrams/diagrams.tsx`.
 */

export interface FlowStep {
  title: string
  detail: string
  parts: readonly string[]
}

export const CONNECTION_FLOW: readonly FlowStep[] = [
  { title: 'User action', detail: 'A click, submit or edit in the UI.', parts: ['browser', 's1'] },
  { title: 'React component', detail: 'The page component handles the event.', parts: ['browser', 's2'] },
  { title: 'TanStack mutation', detail: 'An optimistic mutation is fired.', parts: ['browser', 's3'] },
  {
    title: 'authFetch',
    detail: 'Attaches the Cognito Bearer token and X-Tenant-Id. Tokens come from sign-in / refresh through the API.',
    parts: ['browser', 'req', 'cognito', 'signin', 'api', 's4'],
  },
  { title: 'NestJS REST', detail: 'The request hits the /api controller, which validates the JWT and tenant.', parts: ['api', 'n5', 's5'] },
  { title: 'PostgreSQL', detail: 'Read or write through Prisma.', parts: ['api', 'db', 'prisma', 'n6', 's6'] },
  { title: 'Query invalidation', detail: 'The JSON response lands and the TanStack cache is invalidated.', parts: ['api', 'res', 'browser', 's7'] },
  { title: 'Re-render', detail: 'The screen updates with fresh state.', parts: ['browser', 's8'] },
]

export const HL7_FLOW: readonly FlowStep[] = [
  {
    title: 'Epic sends',
    detail: 'Epic Beacon/Beaker sends raw HL7 v2 over MLLP to the customer-side VPN endpoint.',
    parts: ['emr', 'vpn-endpoint', 'emr-vpn'],
  },
  {
    title: 'Encrypted tunnel',
    detail: 'Traffic is encrypted over an IPsec IKEv2 tunnel to AWS.',
    parts: ['vpn-endpoint', 'tunnel', 'mllp-hop', 'types'],
  },
  {
    title: 'VPN Gateway',
    detail: 'The VPN Gateway terminates the tunnel and routes into the VPC.',
    parts: ['tunnel', 'gateway', 'tunnel-gw'],
  },
  {
    title: 'Receive and ACK',
    detail: 'The MLLP Data Layer receives, stores briefly and returns an ACK (MSA). The API reads it over loopback.',
    parts: ['gateway', 'gw-dl', 'datalayer', 'ack', 'ack-arrow', 'tunnel', 'loopback', 'api'],
  },
  {
    title: 'Observe and protect',
    detail: 'CloudWatch, SNS, Secrets Manager and Systems Manager log, alert and protect.',
    parts: ['mgmt'],
  },
]

export const CICD_FLOW: readonly FlowStep[] = [
  { title: 'Push', detail: 'A push to dev, staging or main starts both pipelines.', parts: ['push'] },
  {
    title: 'Keyless auth',
    detail: 'GitHub Actions assumes an IAM role through OIDC — no stored AWS credentials.',
    parts: ['push', 'push-gha', 'gha'],
  },
  {
    title: 'Build',
    detail: 'API: Docker build, pushed to ECR. Web: pnpm build to static assets.',
    parts: ['gha', 'gha-build', 'build'],
  },
  {
    title: 'Deploy',
    detail: 'API: ECS rolling update behind a circuit breaker that auto-rolls back. Web: upload to S3 and invalidate CloudFront.',
    parts: ['build', 'build-deploy', 'deploy', 'breaker'],
  },
]

/**
 * Re-order to received stock (z8nrz7f7dz), for the User Manual's Re-Order and
 * Purchase Orders page. Part keys are the `k` props in `PurchaseOrderLifecycleDiagram`.
 */
export const PO_LIFECYCLE_FLOW: readonly FlowStep[] = [
  {
    title: 'Material due',
    detail: 'Available + on order is at or below the reorder point, or nothing is on the shelf. The Re-Order tab lists it and its PO icon turns red.',
    parts: ['due'],
  },
  {
    title: 'Re-order calculation',
    detail: 'The PO icon opens the calculation: check the settings against live stock and settle the quantity to order.',
    parts: ['due', 'due-calc', 'calc'],
  },
  {
    title: 'Draft',
    detail: 'Create purchase order saves a draft with its PO number. Edit it freely, or add more materials for the same supplier.',
    parts: ['calc', 'calc-draft', 'draft', 'on-order'],
  },
  {
    title: 'Sent',
    detail: 'Mark as sent freezes the current template onto the order. Every later edit needs a reason and becomes the next revision.',
    parts: ['draft', 'draft-sent', 'sent', 'revise', 'snapshot', 'on-order'],
  },
  {
    title: 'Received',
    detail: 'Mark received records that the goods arrived. The stock itself is booked in with Add lot for each line.',
    parts: ['sent', 'sent-received', 'received', 'received-lot', 'lot'],
  },
  {
    title: 'Cancelled',
    detail: 'A draft or sent order can be cancelled with a reason. Received and cancelled orders are read-only.',
    parts: ['draft', 'sent', 'cancel-draft', 'cancel-sent', 'cancelled'],
  },
]

/** Lit set for a step, or null (everything lit) when no step is active. */
export function litParts(steps: readonly FlowStep[], step: number | null): ReadonlySet<string> | null {
  if (step === null) return null
  const s = steps[step]
  return s ? new Set(s.parts) : null
}
